import {
  AssetRoutineState,
  useFindByQuery,
  useGrippContext,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import { ReactNode, useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { extractAndDeduplicateRoutines } from '../../utils/assetHelpers';
import { RoutineModules } from '../routine';
import { SubHeader, SubHeaderProps } from './headerTypes';

interface SortedRoutineState {
  state?: AssetRoutineState<any>;
  config: any;
  order?: number;
  type: any;
  routines?: WorkspaceRoutine[];
}

// Gap between pills — applied both horizontally and between wrapped rows.
const PILL_GAP = 8;

export const RoutineSubHeader: SubHeader = (props: SubHeaderProps) => {
  const { logger } = useGrippContext();
  const { workspaceAsset } = props;
  const [sorted, setSorted] = useState<SortedRoutineState[] | undefined>();
  const { data } = useFindByQuery({
    modelName: 'WorkspaceRoutineVersion',
    query: GET_ROUTINE_VERSIONS,
    variables: {
      filter: {
        id: {
          in: [
            ...(workspaceAsset.routineState?.map(
              (x) => x.workspaceRoutineVersionId
            ) || []),
            ...(workspaceAsset.routines
              ?.filter(Boolean)
              ?.map((r) => r.currentVersion?.id)
              .filter(Boolean) || []),
          ],
        },
      },
    },
  });

  useEffect(() => {
    if (!data) return;

    const directRoutines =
      workspaceAsset.routines?.filter((r) => r?.deactivated !== true) || [];
    const externalRoutines = extractAndDeduplicateRoutines(workspaceAsset);
    const allRoutines = [...directRoutines, ...externalRoutines];
    const activeRoutineIds = new Set(allRoutines.map((r) => r.id));

    const sortedRoutines = workspaceAsset.routineState
      ?.map((state) => {
        const version = data.items.find(
          (x: { id: string }) => x.id === state.workspaceRoutineVersionId
        );
        const routine = allRoutines?.find(
          (x) => x.id === state.workspaceRoutineId
        );
        const routineOrder = workspaceAsset.routineOrder?.find(
          (x) => x.workspaceRoutineId === state.workspaceRoutineId
        );

        // Include if the routine is active (either directly or embedded in an active workflow)
        const isDirectlyActive = activeRoutineIds.has(state.workspaceRoutineId);
        const isEmbeddedInActiveWorkflow = data.items.some(
          (v: any) =>
            activeRoutineIds.has(v.workspaceRoutine?.id) && // Workflow is active
            v.config?.pages?.some((page: any) =>
              page.questions?.some(
                (q: any) => q.workspaceRoutineId === state.workspaceRoutineId
              )
            )
        );

        return {
          state:
            isDirectlyActive || isEmbeddedInActiveWorkflow ? state : undefined,
          config: version?.config,
          order: routineOrder?.order ?? routine?.order,
          type: version?.type,
          routines: allRoutines,
        };
      })
      .filter((x) => x.state)
      .sort((a, b) => (a.order ?? 0) - (b?.order ?? 0));

    setSorted(sortedRoutines);
  }, [data, workspaceAsset]);

  const renderHeader = (item: SortedRoutineState, index: number): ReactNode => {
    const Header =
      RoutineModules[item.type as keyof typeof RoutineModules]?.header;
    if (!Header) {
      logger?.warning(
        `Routine Module not found (${workspaceAsset.id}) ${JSON.stringify(
          item
        )}`
      );
      return null;
    }
    return (
      <Header
        key={`header_${index}`}
        config={item.config}
        currentState={item.state}
        routines={item.routines}
      />
    );
  };

  if (!sorted) {
    return null;
  }

  // Only keep routines we can actually render so wrapped rows don't reserve
  // space for an empty 0-width slot where a module is missing.
  const renderable = sorted.filter(
    (item) => !!RoutineModules[item.type as keyof typeof RoutineModules]?.header
  );

  // Lay the routine pills out in a wrapping row: when they don't all fit the
  // available width they flow onto the next line rather than scrolling or
  // clipping. The asset header (assetHeader/assetDetails) grows to fit the
  // wrapped rows.
  return (
    <View style={styles.pillRow}>
      {renderable.map((item, index) => (
        <View key={`pill_${index}`} style={styles.pill}>
          {renderHeader(item, index)}
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  pillRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    rowGap: PILL_GAP,
    columnGap: PILL_GAP,
    width: '100%',
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});

const GET_ROUTINE_VERSIONS = `
  query FindWorkspaceRoutineVersions($filter: WorkspaceRoutineVersionFilter) {
    findWorkspaceRoutineVersions(filter: $filter) {
      items {
        config
        id
        type
        workspaceRoutine {
          id
        }
      }
    }
  }
`;
